import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  first,
  map,
  merge,
  Observable,
  of,
  partition,
  ReplaySubject,
  Subject,
  Subscription,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { AuthService } from './auth.service';
import { FireRatingService } from './fire-rating.service';
import { GridChangeService } from './grid-change.service';
import { removeProps } from './helpers/remove-props';
import { InstallationService } from './installation.service';
import { PlantAncillariesService } from './plant-ancillaries.service';
import { QuoteData, QuoteType } from './quote-data';
import {
  CloneQuoteData,
  FreezeQuoteData,
  PrepareQuoteDataForSaving,
  PrepareQuoteDataForWorker,
} from './quote-data-helper';
import { QuoteSessionOptions } from './quote-session-options';
import {
  createQuoteQuery,
  getHeavyLoadTypeDefault,
  getQuoteOptionByIdQuery,
  getQuoteOptionByUuidQuery,
  updateQuoteOptionQuery,
} from './quote.model';
import { TransportService } from './transport.service';
import {
  emailQuote,
  exportCadScript,
  exportQd3,
  generateBudgetQuote,
  generateCalcs,
  generateDistributorQuote,
  generateFloor,
  generateQuote,
  importPolyline,
} from './worker-jobs.model';

export enum DataState {
  Clean = 0,
  Dirty = 1,
}

export interface SessionState {
  forceSave: boolean;
  quoteDataState: DataState;
  drawingState: DataState;
}

@Injectable()
export class QuoteSessionService {
  uuid: string;
  private onNewQuoteStream = new Subject<any>();
  private onSavedStream = new Subject<void>();
  private onSaveErrorStream = new Subject<any>();
  private onSavingStream = new Subject<void>();
  private readDrawingStream = new ReplaySubject<any>(1);
  private readBacksheetStream = new ReplaySubject<any>(1);
  private readAdaptiveDesignBacksheetStream = new ReplaySubject<any>(1);
  private readCalculationsStream = new ReplaySubject<any>(1);
  private saveQuoteDataStream = new Subject<QuoteData>();
  private writeQuoteDataStream = new Subject<QuoteData>();
  private readQuoteDataStream = new ReplaySubject<QuoteData>(1);
  private sessionRequestStream = new ReplaySubject<QuoteSessionOptions>(1);
  private saveQuoteDataSubscription: Subscription;
  private writeQuoteDataSubscription: Subscription;
  private sessionRequestSubscription: Subscription;
  private pendingSave = new BehaviorSubject<boolean>(true);

  private sessionState: SessionState;
  private userProfile: Observable<any>;
  private distributorProfile: Observable<any>;
  private optionId: number;

  get SessionState(): SessionState {
    return this.sessionState;
  }
  get sessionOptions(): Observable<QuoteSessionOptions> {
    return this.sessionRequestStream.asObservable();
  }
  get isQuoteDataDirty(): boolean {
    return this.sessionState.quoteDataState === DataState.Dirty;
  }
  get isDrawingDirty(): boolean {
    return this.sessionState.drawingState === DataState.Dirty;
  }

  get quoteData(): Observable<QuoteData> {
    return this.readQuoteDataStream.pipe(
      map((quoteData: QuoteData) => {
        // make quote data immutable
        FreezeQuoteData(quoteData);
        return quoteData;
      })
    );
  }
  get backsheet(): Observable<any> {
    return this.readBacksheetStream.asObservable();
  }
  get adaptiveDesignBacksheet(): Observable<any> {
    return this.readAdaptiveDesignBacksheetStream.asObservable();
  }
  get calculations(): Observable<any> {
    return this.readCalculationsStream.asObservable();
  }
  get drawing(): Observable<any> {
    return this.readDrawingStream.asObservable();
  }
  get onSaved(): Observable<any> {
    return this.onSavedStream.asObservable();
  }
  get onSaveError(): Observable<any> {
    return this.onSaveErrorStream.asObservable();
  }
  get onSaving(): Observable<any> {
    return this.onSavingStream.asObservable();
  }
  get onNewQuote(): Observable<any> {
    return this.onNewQuoteStream.asObservable();
  }
  get onPendingSave(): BehaviorSubject<boolean> {
    return this.pendingSave;
  }

  constructor(
    private auth: AuthService,
    private apollo: Apollo,
    private gridChangeService: GridChangeService,
    private installationService: InstallationService,
    private transportService: TransportService,
    private fireRatingService: FireRatingService,
    private plantAncillariesService: PlantAncillariesService
  ) {}

  setup(
    settings: Observable<Map<string, any>>,
    userProfile: Observable<any> = of(null),
    distributorProfile: Observable<any> = of(null)
  ): void {
    this.userProfile = userProfile;
    this.distributorProfile = distributorProfile;
    // Save the quote data
    this.pendingSave.next(true);
    this.saveQuoteDataSubscription = this.saveQuoteDataStream
      .pipe(
        debounceTime(500),
        tap(() => this.onSavingStream.next()),
        withLatestFrom(
          this.sessionOptions,
          this.distributorProfile,
          this.userProfile
        ),
        switchMap(([quoteData, sessionOptions, distributor, user]) => {
          if (this.optionId) {
            console.log('Updating QD...');
            return this.apollo.mutate<any>({
              mutation: updateQuoteOptionQuery,
              variables: {
                option: {
                  id: this.optionId,
                  quoteData: PrepareQuoteDataForSaving(quoteData),
                  updatedBy: user.id,
                },
              },
            });
          } else {
            console.log('Creating Quote...');
            return this.apollo.mutate<any>({
              mutation: createQuoteQuery,
              variables: {
                quoteInput: {
                  distributorId: distributor.id,
                  type: QuoteType[sessionOptions.quoteType],
                  createdBy: user.id,
                },
                quoteDataInput: PrepareQuoteDataForSaving(quoteData),
              },
            });
          }
        })
      )
      .subscribe({
        next: ({ data }) => {
          if (data.createQuote) {
            this.optionId = data.createQuote.quoteOption.id;
            this.uuid = data.createQuote.quoteOption.uuid;
            this.onNewQuoteStream.next({
              quoteId: data.createQuote.quote.id,
              revisionId: data.createQuote.quoteRevision.id,
              optionId: data.createQuote.quoteOption.id,
              uuid: data.createQuote.quoteOption.uuid,
            });
            this.readQuoteDataStream.next(
              data.createQuote.quoteOption.quoteData
            );
            console.log(
              `Successfully created new quote (${data.createQuote.quoteOption.id})`
            );
          } else if (data.updateQuoteOption) {
            console.log(
              `Successfully updated QD (${data.updateQuoteOption.id})`
            );
          }
          this.onSavedStream.next();
          this.pendingSave.next(false);
        },
        error: (error) => this.onSaveErrorStream.next(error),
      });

    const heavyLoadTypeDefault = this.apollo
      .query<any>({
        query: getHeavyLoadTypeDefault,
      })
      .pipe(first());

    // Set/Change the quote data
    this.writeQuoteDataSubscription = this.writeQuoteDataStream
      .pipe(
        distinctUntilChanged(),
        withLatestFrom(heavyLoadTypeDefault),
        switchMap(([qd, heavyLoadTypeDefault]) =>
          this.recalculate(qd, heavyLoadTypeDefault.data.loadTypeDefault)
        ),
        withLatestFrom(this.sessionRequestStream)
      )
      .subscribe(([newQd, options]) => {
        this.readQuoteDataStream.next(newQd); // update our subscribers
        if (
          this.sessionState.forceSave ||
          [QuoteType.Internal, QuoteType.Distributor].some(
            (x) => x === options.quoteType
          )
        ) {
          this.saveQuoteDataStream.next(newQd); // if we care about saving, save it
        }
      });

    // Create a new session
    this.sessionRequestSubscription = this.sessionRequestStream
      .pipe(
        distinctUntilChanged(),
        withLatestFrom(settings),
        switchMap(([options, currentSettings]) =>
          this.createSession(currentSettings, options)
        ),
        switchMap(this.recalculate.bind(this))
      )
      .subscribe((quoteData: QuoteData) => {
        this.readQuoteDataStream.next(quoteData);
        this.sessionState = {
          forceSave: false,
          quoteDataState: DataState.Dirty,
          drawingState: DataState.Dirty,
          backsheetState: DataState.Dirty,
          calcsState: DataState.Dirty,
        } as SessionState;
      });
  }

  destroy(): void {
    if (this.saveQuoteDataSubscription) {
      this.saveQuoteDataSubscription.unsubscribe();
    }
    if (this.writeQuoteDataSubscription) {
      this.writeQuoteDataSubscription.unsubscribe();
    }
    if (this.sessionRequestSubscription) {
      this.sessionRequestSubscription.unsubscribe();
    }
  }

  private createSession(
    settings: Map<string, any>,
    opt: QuoteSessionOptions
  ): Observable<QuoteData> {
    if (opt.quoteId && opt.revisionId && opt.optionId) {
      return this.loadOldQuote(opt);
    } else if (opt.optionUuid) {
      return this.loadOldDistributorQuote(opt);
    }

    return this.createNewQuote(settings, opt);
  }

  private recalculate(
    quoteData: QuoteData,
    heavyLoadTypeDefaults: any
  ): Observable<QuoteData> {
    return this.gridChangeService
      .calculate(quoteData, heavyLoadTypeDefaults)
      .pipe(
        switchMap((qd) => this.transportService.calculate(qd)),
        switchMap((qd) => this.fireRatingService.calculate(qd)),
        switchMap((qd) => this.installationService.calculate(qd)),
        switchMap((qd) => this.plantAncillariesService.calculate(qd))
      );
  }

  private loadOldQuote(opt: QuoteSessionOptions): Observable<QuoteData> {
    this.optionId = opt.optionId;
    return this.apollo
      .query<any>({
        query: getQuoteOptionByIdQuery,
        variables: {
          id: opt.optionId,
        },
      })
      .pipe(
        tap(({ data }) => (this.uuid = data.quoteOption.uuid)),
        map(({ data }) => removeProps(data.quoteOption.quoteData, '__typename'))
      );
  }

  private loadOldDistributorQuote(
    opt: QuoteSessionOptions
  ): Observable<QuoteData> {
    return this.apollo
      .query<any>({
        query: getQuoteOptionByUuidQuery,
        variables: {
          uuid: opt.optionUuid,
        },
      })
      .pipe(
        tap(({ data }) => {
          this.optionId = data.getOneByUuidDistributor.id;
          this.uuid = data.getOneByUuidDistributor.uuid;
        }),
        map(({ data }) =>
          removeProps(data.getOneByUuidDistributor.quoteData, '__typename')
        )
      );
  }

  private createNewQuote(
    settings: Map<string, any>,
    opt: QuoteSessionOptions
  ): Observable<QuoteData> {
    this.optionId = null;
    let setting: any;
    switch (opt.quoteType) {
      case QuoteType.Budget:
        setting = settings.get('budgetDefaultQd');
        break;
      case QuoteType.Distributor:
        setting = settings.get('distributorDefaultQd');
        break;
      case QuoteType.Internal:
        setting = settings.get('internalDefaultQd');
        break;
      default:
        return;
    }

    const [loggedIn, notLoggedIn] = partition(
      this.auth.isAuthenticated$,
      (isLoggedIn) => isLoggedIn
    );

    return merge(
      loggedIn.pipe(
        // user is logged in, fetch distributor profile and apply changes
        switchMap(() => of(setting)),
        withLatestFrom(this.distributorProfile),
        map(([quoteData, dp]) => {
          const qd = CloneQuoteData(quoteData);
          // set the type of quote
          qd.GeneralData.QuoteType = opt.quoteType;
          // set the company name if the user is logged in
          qd.GeneralData.DistributorName = dp && dp.name ? dp.name : null;
          // set the gp and fire-rating gp
          qd.GpData.TargetGp =
            opt.quoteType === QuoteType.Distributor
              ? dp.distributorPortalGp
              : dp.gp;
          qd.GpData.FireRatingGp =
            opt.quoteType === QuoteType.Distributor
              ? dp.distributorPortalFireRatingGp
              : dp.fireRatingGp;

          return qd;
        })
      ),
      notLoggedIn.pipe(
        // user is not logged in, do not apply changes
        switchMap(() => of(CloneQuoteData(setting)))
      )
    );
  }

  newSession(
    quoteType: QuoteType,
    quoteId: number = null,
    revisionId: number = null,
    optionId: number = null,
    optionUuid: string = null
  ): void {
    this.endSession();
    this.auth.isAuthenticated$.pipe(first()).subscribe((isLoggedIn) => {
      if (isLoggedIn) {
        this.pendingSave.next(false);
        this.userProfile
          .pipe(first())
          .subscribe((profile) =>
            this.sessionRequestStream.next(
              new QuoteSessionOptions(
                quoteType,
                quoteId,
                revisionId,
                optionId,
                profile.name,
                optionUuid
              )
            )
          );
      } else {
        this.pendingSave.next(true);
        this.sessionRequestStream.next(
          new QuoteSessionOptions(
            quoteType,
            quoteId,
            revisionId,
            optionId,
            'guest',
            optionUuid
          )
        );
      }
    });
  }

  endSession(): void {
    this.readDrawingStream.next(null);
    this.readBacksheetStream.next(null);
    this.readAdaptiveDesignBacksheetStream.next(null);
  }

  setQuoteData(
    newQd: any,
    setDirty: boolean = true,
    forceSave: boolean = false
  ): void {
    if (setDirty) {
      this.sessionState.quoteDataState = DataState.Dirty;
    }
    this.sessionState.forceSave = forceSave;
    this.writeQuoteDataStream.next(newQd);
  }

  setDrawing(drawing: any): void {
    this.sessionState.quoteDataState = DataState.Clean;
    this.sessionState.drawingState = DataState.Dirty;
    this.readDrawingStream.next(drawing);
  }

  setBacksheet(backsheet: any, adaptiveDesignBacksheet: any = null): void {
    this.sessionState.quoteDataState = DataState.Clean;
    this.sessionState.drawingState = DataState.Clean;
    this.readBacksheetStream.next(backsheet);
    this.readAdaptiveDesignBacksheetStream.next(adaptiveDesignBacksheet);
  }

  setCalculations(calculations: any): void {
    this.sessionState.quoteDataState = DataState.Clean;
    this.sessionState.drawingState = DataState.Clean;
    this.readCalculationsStream.next(calculations);
  }

  runJob(
    jobName: string,
    payload: any = null,
    onSuccess: (data: any) => void,
    onErrors: (errors: string[]) => void,
    onWarnings: (warnings: string[]) => void
  ): void {
    this.sessionOptions
      .pipe(withLatestFrom(this.quoteData), first())
      .subscribe(([sessionOptions, quoteData]) => {
        // send quoteData for budget quotes,
        // otherwise send quote option id
        let mutation: DocumentNode;
        const variables: any = {
          sessionData: {
            username: sessionOptions.username,
            sessionId: sessionOptions.uuid,
          },
          quoteOption: this.optionId,
        };
        switch (jobName) {
          case 'generateFloor':
            if (sessionOptions.quoteType === QuoteType.Budget) {
              jobName = 'generateBudgetQuote';
              mutation = generateBudgetQuote;
              variables.quoteData = PrepareQuoteDataForWorker(quoteData);
            } else if (sessionOptions.quoteType === QuoteType.Distributor) {
              jobName = 'generateDistributorQuote';
              mutation = generateDistributorQuote;
            } else {
              mutation = generateFloor;
            }
            break;
          case 'exportCadScript':
            mutation = exportCadScript;
            break;
          case 'generateQuote':
            mutation = generateQuote;
            break;
          case 'generateCalcs':
            mutation = generateCalcs;
            break;
          case 'exportQd3':
            mutation = exportQd3;
            break;
          case 'emailQuote':
            mutation = emailQuote;
            variables.toEmailAddress = payload.toEmailAddress;
            break;
          case 'importPolyline':
            mutation = importPolyline;
            variables.script = payload.script;
            break;
        }

        this.apollo
          .mutate<any>({
            mutation,
            variables,
          })
          .pipe(first())
          .subscribe({
            next: ({ data }) => {
              if (data[jobName].warnings) {
                onWarnings(data[jobName].warnings);
              } else {
                onSuccess(data[jobName].result);
              }
            },
            error: (error) => onErrors(error),
          });
      });
  }
}
